/**
 * Admin > Site content > Homepage — edit the TEXT of each homepage section
 * and toggle each section on/off. Prices are NOT edited here. Text fields allow
 * **bold** and [label](url) only.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Save, ExternalLink, Eye, EyeOff, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

const SECTIONS = [
  {
    key: "homepage.hero",
    title: "Hero",
    fields: [
      { name: "headline", label: "Headline", type: "textarea" },
      { name: "subheadline", label: "Sub-headline", type: "textarea" },
      { name: "button_label", label: "Button label", type: "text" },
      { name: "button_url", label: "Button URL", type: "text" },
      { name: "subline", label: "Small line under the button", type: "text" },
    ],
  },
  {
    key: "homepage.sits_on_top",
    title: "Sits on top of your existing prep",
    fields: [
      { name: "badge", label: "Badge", type: "text" },
      { name: "heading", label: "Heading", type: "textarea" },
      { name: "body", label: "Body", type: "textarea" },
      { name: "bullets", label: "Bullets", type: "list" },
      { name: "disclaimer", label: "Disclaimer", type: "textarea" },
    ],
  },
  {
    key: "homepage.comparison",
    title: "Comparison table",
    fields: [
      { name: "heading", label: "Heading", type: "textarea" },
      { name: "subheading", label: "Sub-heading", type: "text" },
      { name: "col_us", label: "Column — us", type: "text" },
      { name: "col_them", label: "Column — them", type: "text" },
      { name: "rows", label: "Rows", type: "rows" },
    ],
  },
  {
    key: "homepage.pricing",
    title: "Pricing (text only — prices come from plan config)",
    fields: [
      { name: "heading", label: "Heading", type: "textarea" },
      { name: "subheading", label: "Sub-heading", type: "textarea" },
      { name: "guarantee_line", label: "Guarantee line", type: "text" },
    ],
  },
  {
    key: "homepage.commitment",
    title: "Readiness commitment",
    fields: [
      { name: "heading", label: "Heading", type: "textarea" },
      { name: "body", label: "Body", type: "textarea" },
      { name: "steps", label: "Steps", type: "steps" },
      { name: "footnote", label: "Footnote", type: "textarea" },
    ],
  },
  {
    key: "homepage.final_cta",
    title: "Final call-to-action",
    fields: [
      { name: "heading", label: "Heading", type: "textarea" },
      { name: "body", label: "Body", type: "textarea" },
      { name: "button_label", label: "Button label", type: "text" },
      { name: "button_url", label: "Button URL", type: "text" },
      { name: "subline", label: "Small line under the button", type: "text" },
    ],
  },
];

const SectionForm = ({ def, value, onSaved }) => {
  const [form, setForm] = useState(value || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(value || {}); }, [value]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/site-settings/${encodeURIComponent(def.key)}`, { value: form, is_public: true });
      toast.success(`Saved ${def.title}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const shown = form.show !== false;

  return (
    <div className="p-4 rounded-lg border border-border space-y-3" data-testid={`content-section-${def.key}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{def.title}</h3>
        <button
          onClick={() => set("show", !shown)}
          data-testid={`content-show-${def.key}`}
          className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${shown ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}
        >
          {shown ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {shown ? "Shown" : "Hidden"}
        </button>
      </div>

      {def.fields.map((f) => (
        <div key={f.name} className="space-y-1">
          <label className="text-xs text-muted-foreground">{f.label}</label>
          {f.type === "textarea" && (
            <textarea value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} rows={2}
              data-testid={`field-${def.key}-${f.name}`}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          )}
          {f.type === "text" && (
            <input value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)}
              data-testid={`field-${def.key}-${f.name}`}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          )}
          {f.type === "list" && (
            <div className="space-y-1">
              {(form[f.name] || []).map((item, i) => (
                <input key={i} value={item}
                  onChange={(e) => set(f.name, (form[f.name] || []).map((x, j) => (j === i ? e.target.value : x)))}
                  className="w-full px-3 py-1.5 rounded-lg bg-background border border-border text-sm" />
              ))}
            </div>
          )}
          {f.type === "rows" && (
            <div className="space-y-1">
              {(form[f.name] || []).map((row, i) => (
                <div key={i} className="grid grid-cols-3 gap-1">
                  {["label", "us", "them"].map((c) => (
                    <input key={c} value={row[c] ?? ""} placeholder={c}
                      onChange={(e) => set(f.name, (form[f.name] || []).map((x, j) => (j === i ? { ...x, [c]: e.target.value } : x)))}
                      className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs" />
                  ))}
                </div>
              ))}
            </div>
          )}
          {f.type === "steps" && (
            <div className="space-y-1">
              {(form[f.name] || []).map((st, i) => (
                <div key={i} className="grid grid-cols-2 gap-1">
                  {["t", "d"].map((c) => (
                    <input key={c} value={st[c] ?? ""} placeholder={c === "t" ? "title" : "detail"}
                      onChange={(e) => set(f.name, (form[f.name] || []).map((x, j) => (j === i ? { ...x, [c]: e.target.value } : x)))}
                      className="px-2 py-1.5 rounded-lg bg-background border border-border text-xs" />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <button onClick={save} disabled={saving} data-testid={`content-save-${def.key}`}
        className="flex items-center gap-1 text-sm px-4 py-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-50">
        <Save className="w-4 h-4" /> Save
      </button>
    </div>
  );
};

const TestimonialsEditor = ({ value, onSaved }) => {
  const base = { show_section: true, heading: "", subtitle: "", items: [] };
  const [form, setForm] = useState({ ...base, ...(value || {}) });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ ...base, ...(value || {}) }); /* eslint-disable-next-line */ }, [value]);
  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), { name: "", country: "", quote: "", consent_date: "", show: true }] }));
  const delItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/site-settings/homepage.testimonials`, { value: form, is_public: true }); toast.success("Testimonials saved"); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="p-4 rounded-lg border border-border space-y-3" data-testid="testimonials-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Testimonials</h3>
        <button onClick={() => setForm((f) => ({ ...f, show_section: !(f.show_section !== false) }))} data-testid="testimonials-show" className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${form.show_section !== false ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
          {form.show_section !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {form.show_section !== false ? "Shown" : "Hidden"}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">A testimonial appears on the homepage only if its <b>Show</b> is on AND it has a <b>consent date</b>.</p>
      <input value={form.heading} onChange={(e) => setForm((f) => ({ ...f, heading: e.target.value }))} placeholder="Section heading" className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
      <input value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} placeholder="Subtitle" className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
      {(form.items || []).map((it, i) => (
        <div key={i} className="p-3 rounded-lg border border-border space-y-2" data-testid={`testimonial-item-${i}`}>
          <div className="grid grid-cols-2 gap-2">
            <input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} placeholder="Name" className="px-2 py-1.5 rounded-lg bg-background border border-border text-sm" />
            <input value={it.country} onChange={(e) => setItem(i, "country", e.target.value)} placeholder="Country" className="px-2 py-1.5 rounded-lg bg-background border border-border text-sm" />
          </div>
          <textarea value={it.quote} onChange={(e) => setItem(i, "quote", e.target.value)} placeholder="Quote" rows={2} className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-sm" />
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-xs flex items-center gap-1">Consent date <input type="date" value={it.consent_date || ""} onChange={(e) => setItem(i, "consent_date", e.target.value)} data-testid={`testimonial-consent-${i}`} className="px-2 py-1 rounded bg-background border border-border text-xs" /></label>
            <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={it.show === true} onChange={(e) => setItem(i, "show", e.target.checked)} data-testid={`testimonial-show-${i}`} /> Show</label>
            <button onClick={() => delItem(i)} className="text-xs text-destructive flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={addItem} className="text-sm px-3 py-1.5 rounded-lg border border-border flex items-center gap-1"><Plus className="w-4 h-4" /> Add testimonial</button>
        <button onClick={save} disabled={saving} data-testid="testimonials-save" className="text-sm px-4 py-1.5 rounded-lg bg-primary text-primary-foreground flex items-center gap-1 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
};

const TrustStripEditor = ({ value, onSaved }) => {
  const base = { show_section: true, cards: [] };
  const [form, setForm] = useState({ ...base, ...(value || {}) });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ ...base, ...(value || {}) }); /* eslint-disable-next-line */ }, [value]);
  const setCard = (i, k, v) => setForm((f) => ({ ...f, cards: f.cards.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addCard = () => setForm((f) => (f.cards.length >= 6 ? f : ({ ...f, cards: [...f.cards, { type: "static", title: "", sub_line: "", link_url: "" }] })));
  const delCard = (i) => setForm((f) => ({ ...f, cards: f.cards.filter((_, j) => j !== i) }));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/site-settings/homepage.trust_strip`, { value: form, is_public: true }); toast.success("Trust strip saved"); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <div className="p-4 rounded-lg border border-border space-y-3" data-testid="trust-strip-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Trust strip (up to 6 cards)</h3>
        <button onClick={() => setForm((f) => ({ ...f, show_section: !(f.show_section !== false) }))} className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${form.show_section !== false ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}`}>
          {form.show_section !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {form.show_section !== false ? "Shown" : "Hidden"}
        </button>
      </div>
      {(form.cards || []).map((c, i) => (
        <div key={i} className="p-3 rounded-lg border border-border space-y-2" data-testid={`trust-card-${i}`}>
          <select value={c.type || "static"} onChange={(e) => setCard(i, "type", e.target.value)} className="px-2 py-1.5 rounded-lg bg-background border border-border text-sm">
            <option value="static">Static text</option>
            <option value="live_count">Live member count</option>
          </select>
          {c.type !== "live_count" && (
            <input value={c.title} onChange={(e) => setCard(i, "title", e.target.value)} placeholder="Title" className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-sm" />
          )}
          <input value={c.sub_line} onChange={(e) => setCard(i, "sub_line", e.target.value)} placeholder="Sub line (optional)" className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-sm" />
          <input value={c.link_url} onChange={(e) => setCard(i, "link_url", e.target.value)} placeholder="Link URL (optional)" className="w-full px-2 py-1.5 rounded-lg bg-background border border-border text-sm" />
          <button onClick={() => delCard(i)} className="text-xs text-destructive flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={addCard} disabled={(form.cards || []).length >= 6} className="text-sm px-3 py-1.5 rounded-lg border border-border flex items-center gap-1 disabled:opacity-50" data-testid="trust-add-card"><Plus className="w-4 h-4" /> Add card</button>
        <button onClick={save} disabled={saving} data-testid="trust-save" className="text-sm px-4 py-1.5 rounded-lg bg-primary text-primary-foreground flex items-center gap-1 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
};

const FaqTags = ({ tags, onAdd, onRemove }) => {
  const [val, setVal] = useState("");
  const commit = () => { if (val.trim()) { onAdd(val); setVal(""); } };
  return (
    <div className="flex items-center gap-1.5 flex-wrap text-xs" data-testid="faq-tags">
      <span className="text-muted-foreground">Show on:</span>
      {(tags || []).map((t) => (
        <span key={t} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-secondary border border-border">
          {t}
          <button type="button" onClick={() => onRemove(t)} aria-label={`remove ${t}`} className="w-4 h-4 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-destructive">×</button>
        </span>
      ))}
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); } }}
        onBlur={commit}
        placeholder="add tag…"
        data-testid="faq-tag-input"
        className="w-28 px-2 py-1 rounded bg-background border border-border"
      />
    </div>
  );
};

const FaqManager = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try { const d = await api.get(`/admin/faq`); setItems(d.items || []); } catch { toast.error("Failed to load FAQ"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (id, patch) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const saveItem = async (it) => {
    try {
      await api.put(`/admin/faq/${it.id}`, { question: it.question, answer: it.answer, pages: it.pages, published: it.published, order: it.order });
      toast.success("Saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const addItem = async () => {
    try { await api.post(`/admin/faq`, { question: "New question", answer: "Answer", pages: ["home"], published: false }); load(); }
    catch { toast.error("Add failed"); }
  };
  const delItem = async (id) => { if (!window.confirm("Delete this FAQ item?")) return; try { const ax = (await import("axios")).default; await ax.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/faq/${id}`, { withCredentials: true }); load(); } catch { toast.error("Delete failed"); } };
  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= items.length) return;
    const ids = items.map((x) => x.id); [ids[i], ids[j]] = [ids[j], ids[i]];
    try { await api.post(`/admin/faq/reorder`, { ids }); load(); } catch { toast.error("Reorder failed"); }
  };
  const addTag = (it, raw) => {
    const t = (raw || "").trim().toLowerCase().replace(/\s+/g, "-");
    if (!t || it.pages?.includes(t)) return;
    update(it.id, { pages: [...(it.pages || []), t] });
  };
  const removeTag = (it, t) => update(it.id, { pages: (it.pages || []).filter((p) => p !== t) });

  return (
    <div className="space-y-3" data-testid="faq-manager">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">FAQ items</h2>
        <button onClick={addItem} data-testid="faq-add" className="text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground flex items-center gap-1"><Plus className="w-4 h-4" /> Add item</button>
      </div>
      {items.map((it, i) => (
        <div key={it.id} className="p-4 rounded-lg border border-border space-y-2" data-testid={`faq-edit-${i}`}>
          <div className="flex items-center gap-2">
            <input value={it.question} onChange={(e) => update(it.id, { question: e.target.value })} className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm font-medium" />
            <button onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-secondary"><ArrowUp className="w-4 h-4" /></button>
            <button onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-secondary"><ArrowDown className="w-4 h-4" /></button>
          </div>
          <textarea value={it.answer} onChange={(e) => update(it.id, { answer: e.target.value })} rows={2} data-testid={`faq-answer-${i}`} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          <FaqTags tags={it.pages || []} onAdd={(t) => addTag(it, t)} onRemove={(t) => removeTag(it, t)} />
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <label className="flex items-center gap-1"><input type="checkbox" checked={it.published === true} onChange={(e) => update(it.id, { published: e.target.checked })} data-testid={`faq-published-${i}`} /> published</label>
            <button onClick={() => saveItem(it)} data-testid={`faq-save-${i}`} className="px-3 py-1 rounded-lg bg-primary text-primary-foreground flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Save</button>
            <button onClick={() => delItem(it.id)} className="px-2 py-1 rounded-lg text-destructive flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
};

const BLOCK_HELP = 'Blocks = JSON array. Types: {"type":"heading","text":"..."}, {"type":"text","text":"**bold** and [link](/url)"}, {"type":"button","label":"Go","url":"/signup?utm_source=x"}, {"type":"two_cards","cards":[{"title":"","body":""},{"title":"","body":""}]}, {"type":"timeline","items":[{"title":"","body":""}]}, {"type":"faq","faq_page":"pricing"}, {"type":"live_count","sub_line":"and counting"}, {"type":"image","url":"https://...","alt":""}';

const PagesManager = () => {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [blocksText, setBlocksText] = useState("[]");
  const load = useCallback(async () => {
    try { const d = await api.get(`/admin/pages`); setPages(d.pages || []); } catch { toast.error("Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing({ slug: "", type: "page", title: "", meta_description: "", og_image_url: "", status: "draft", show_in_footer: false, noindex: false }); setBlocksText("[]"); };
  const startEdit = (p) => { setEditing({ ...p }); setBlocksText(JSON.stringify(p.blocks || [], null, 2)); };
  const save = async () => {
    let blocks;
    try { blocks = JSON.parse(blocksText || "[]"); } catch { return toast.error("Blocks is not valid JSON"); }
    const body = { ...editing, blocks };
    try {
      if (editing.id) await api.put(`/admin/pages/${editing.id}`, body);
      else await api.post(`/admin/pages`, body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const toggleStatus = async (p) => {
    try { await api.put(`/admin/pages/${p.id}`, { ...p, status: p.status === "published" ? "draft" : "published" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete draft "${p.slug}"?`)) return;
    try { const ax = (await import("axios")).default; await ax.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/pages/${p.id}`, { withCredentials: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const previewUrl = (p) => `${p.type === "guide" ? "/guides/" : "/"}${p.slug}?preview=1`;

  if (editing) {
    const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
    return (
      <div className="space-y-3" data-testid="page-editor">
        <div className="grid grid-cols-2 gap-2">
          <input value={editing.slug} onChange={(e) => set("slug", e.target.value)} placeholder="slug" data-testid="page-slug" className="px-3 py-2 rounded-lg bg-background border border-border text-sm" />
          <select value={editing.type} onChange={(e) => set("type", e.target.value)} className="px-3 py-2 rounded-lg bg-background border border-border text-sm"><option value="page">page (/slug)</option><option value="guide">guide (/guides/slug)</option></select>
        </div>
        <input value={editing.title} onChange={(e) => set("title", e.target.value)} placeholder="Title" className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <input value={editing.meta_description} onChange={(e) => set("meta_description", e.target.value)} placeholder="Meta description" className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <input value={editing.og_image_url} onChange={(e) => set("og_image_url", e.target.value)} placeholder="OG image URL" className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" />
        <textarea value={blocksText} onChange={(e) => setBlocksText(e.target.value)} rows={10} data-testid="page-blocks" className="w-full px-3 py-2 rounded-lg bg-background border border-border text-xs font-mono" />
        <p className="text-[11px] text-muted-foreground">{BLOCK_HELP}</p>
        <div className="flex items-center gap-4 text-xs">
          <label className="flex items-center gap-1"><input type="checkbox" checked={editing.show_in_footer} onChange={(e) => set("show_in_footer", e.target.checked)} /> show in footer</label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={editing.noindex} onChange={(e) => set("noindex", e.target.checked)} /> noindex</label>
        </div>
        <div className="flex gap-2">
          <button onClick={save} data-testid="page-save" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm">Save</button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-border text-sm">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pages-manager">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">Pages & guides</h2>
        <button onClick={startNew} data-testid="page-new" className="text-sm px-3 py-2 rounded-lg bg-primary text-primary-foreground flex items-center gap-1"><Plus className="w-4 h-4" /> New</button>
      </div>
      {pages.map((p) => (
        <div key={p.id} className="p-3 rounded-lg border border-border flex items-center justify-between gap-2" data-testid={`page-row-${p.slug}`}>
          <div>
            <div className="font-medium text-sm">{p.title || p.slug} <span className="text-xs text-muted-foreground">/{p.type === "guide" ? "guides/" : ""}{p.slug}</span></div>
            <div className="text-xs text-muted-foreground">{p.status}{p.show_in_footer ? " · footer" : ""}{p.noindex ? " · noindex" : ""}</div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <a href={previewUrl(p)} target="_blank" rel="noreferrer" className="underline">Preview</a>
            <button onClick={() => startEdit(p)} className="px-2 py-1 rounded border border-border">Edit</button>
            <button onClick={() => toggleStatus(p)} data-testid={`page-toggle-${p.slug}`} className="px-2 py-1 rounded border border-border">{p.status === "published" ? "Unpublish" : "Publish"}</button>
            {p.status !== "published" && <button onClick={() => del(p)} className="px-2 py-1 rounded text-destructive">Delete</button>}
          </div>
        </div>
      ))}
    </div>
  );
};

const PLAN_KEY_OPTIONS = [
  { v: "", label: "Free (no checkout → sign up)" },
  { v: "monthly", label: "Pro - Monthly plan" },
  { v: "six_month", label: "Pro - 6 Months plan" },
  { v: "march_pass_2027", label: "March 2027 Pass (one-time)" },
];

const PriceCardsEditor = ({ value, onSaved }) => {
  const [cards, setCards] = useState(() => (value?.cards ? JSON.parse(JSON.stringify(value.cards)) : []));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setCards(value?.cards ? JSON.parse(JSON.stringify(value.cards)) : []); }, [value]);

  const setCard = (i, patch) => setCards((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const moveCard = (i, dir) => setCards((cs) => {
    const j = i + dir; if (j < 0 || j >= cs.length) return cs;
    const next = [...cs]; [next[i], next[j]] = [next[j], next[i]];
    return next.map((c, idx) => ({ ...c, sort_order: idx }));
  });
  const addCard = () => setCards((cs) => [...cs, { id: `card_${Date.now()}`, title: "New plan", price_text: "$0", period_text: "", features: [], button_label: "Choose", plan_key: "", highlight: false, visible: true, sort_order: cs.length }]);
  const delCard = (i) => { if (!window.confirm("Remove this card?")) return; setCards((cs) => cs.filter((_, j) => j !== i)); };

  const setFeature = (ci, fi, val) => setCard(ci, { features: cards[ci].features.map((f, j) => (j === fi ? val : f)) });
  const addFeature = (ci) => setCard(ci, { features: [...(cards[ci].features || []), ""] });
  const delFeature = (ci, fi) => setCard(ci, { features: cards[ci].features.filter((_, j) => j !== fi) });
  const moveFeature = (ci, fi, dir) => {
    const fs = [...(cards[ci].features || [])]; const j = fi + dir;
    if (j < 0 || j >= fs.length) return; [fs[fi], fs[j]] = [fs[j], fs[fi]];
    setCard(ci, { features: fs });
  };

  const save = async () => {
    setSaving(true);
    try {
      const normalized = cards.map((c, i) => ({ ...c, sort_order: i }));
      await api.put(`/admin/site-settings/pricing_cards`, { value: { cards: normalized }, is_public: true });
      toast.success("Price cards saved · live within a minute");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="price-cards-editor">
      <p className="text-xs text-muted-foreground">
        These cards render on the homepage, /pricing, the upgrade modal and the in-app paywall.
        Price text is display only — checkout always uses the linked plan&apos;s Stripe price.
        A card linked to a switched-off plan (e.g. the March Pass while its feature is off) is hidden automatically.
      </p>
      {cards.map((c, i) => (
        <div key={c.id || i} className="p-4 rounded-lg border border-border space-y-3" data-testid={`price-card-${i}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-mono px-2 py-0.5 rounded bg-secondary">{c.id}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => moveCard(i, -1)} className="p-1.5 rounded hover:bg-secondary" data-testid={`price-card-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => moveCard(i, 1)} className="p-1.5 rounded hover:bg-secondary" data-testid={`price-card-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
              <button onClick={() => delCard(i)} className="p-1.5 rounded text-destructive hover:bg-destructive/10" data-testid={`price-card-del-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="space-y-1"><span className="text-xs text-muted-foreground">Title</span>
              <input value={c.title ?? ""} onChange={(e) => setCard(i, { title: e.target.value })} data-testid={`price-card-title-${i}`} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" /></label>
            <label className="space-y-1"><span className="text-xs text-muted-foreground">Price text</span>
              <input value={c.price_text ?? ""} onChange={(e) => setCard(i, { price_text: e.target.value })} data-testid={`price-card-pricetext-${i}`} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" /></label>
            <label className="space-y-1"><span className="text-xs text-muted-foreground">Period text</span>
              <input value={c.period_text ?? ""} onChange={(e) => setCard(i, { period_text: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" placeholder="/month" /></label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="space-y-1"><span className="text-xs text-muted-foreground">Button label</span>
              <input value={c.button_label ?? ""} onChange={(e) => setCard(i, { button_label: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm" /></label>
            <label className="space-y-1"><span className="text-xs text-muted-foreground">Linked plan (checkout)</span>
              <select value={c.plan_key ?? ""} onChange={(e) => setCard(i, { plan_key: e.target.value })} data-testid={`price-card-plan-${i}`} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm">
                {PLAN_KEY_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select></label>
          </div>
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Feature lines</span>
            {(c.features || []).map((f, fi) => (
              <div key={fi} className="flex items-center gap-1">
                <input value={f} onChange={(e) => setFeature(i, fi, e.target.value)} data-testid={`price-card-${i}-feature-${fi}`} className="flex-1 px-3 py-1.5 rounded-lg bg-background border border-border text-sm" />
                <button onClick={() => moveFeature(i, fi, -1)} className="p-1 rounded hover:bg-secondary"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button onClick={() => moveFeature(i, fi, 1)} className="p-1 rounded hover:bg-secondary"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button onClick={() => delFeature(i, fi)} className="p-1 rounded text-destructive hover:bg-destructive/10"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button onClick={() => addFeature(i)} data-testid={`price-card-${i}-add-feature`} className="text-xs px-2.5 py-1 rounded-lg border border-border flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add line</button>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <label className="flex items-center gap-1"><input type="checkbox" checked={c.highlight === true} onChange={(e) => setCard(i, { highlight: e.target.checked })} data-testid={`price-card-highlight-${i}`} /> Highlight (Best value)</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={c.visible !== false} onChange={(e) => setCard(i, { visible: e.target.checked })} data-testid={`price-card-visible-${i}`} /> Visible</label>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={addCard} data-testid="price-card-add" className="text-sm px-3 py-1.5 rounded-lg border border-border flex items-center gap-1"><Plus className="w-4 h-4" /> Add card</button>
        <button onClick={save} disabled={saving} data-testid="price-cards-save" className="text-sm px-4 py-1.5 rounded-lg bg-primary text-primary-foreground flex items-center gap-1 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
};

const AdminSiteContentPage = () => {
  const [byKey, setByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("homepage");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/admin/site-settings`);
      const map = {};
      (data.settings || []).forEach((s) => { map[s.key] = s.value; });
      setByKey(map);
    } catch { toast.error("Failed to load content"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-muted-foreground" data-testid="site-content-loading">Loading…</div>;

  return (
    <div className="p-6 space-y-6 max-w-3xl" data-testid="admin-site-content">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Site content</h1>
          <p className="text-sm text-muted-foreground">Edit homepage text and FAQ with no deploy. Use **bold** and [label](url) only. Changes appear on the live site within a minute.</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" data-testid="view-live-btn" className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-border hover:bg-secondary">
          <ExternalLink className="w-4 h-4" /> View live
        </a>
      </div>

      <div className="flex gap-2 border-b border-border">
        {[["homepage", "Homepage"], ["cards", "Price cards"], ["faq", "FAQ"], ["pages", "Pages"]].map(([v, label]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`content-tab-${v}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === v ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "homepage" && (
        <div className="space-y-6">
          {SECTIONS.map((def) => (
            <SectionForm key={def.key} def={def} value={byKey[def.key]} onSaved={load} />
          ))}
          <TestimonialsEditor value={byKey["homepage.testimonials"]} onSaved={load} />
          <TrustStripEditor value={byKey["homepage.trust_strip"]} onSaved={load} />
        </div>
      )}

      {tab === "faq" && <FaqManager />}
      {tab === "pages" && <PagesManager />}
      {tab === "cards" && <PriceCardsEditor value={byKey["pricing_cards"]} onSaved={load} />}
    </div>
  );
};

export default AdminSiteContentPage;
